import { TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SG7TacticalPanel } from "./sg7";
import { cn } from "@/lib/utils";
import type { SafeNumbers } from "@/hooks/useAnalysis";

interface KeyFact {
  rank?: number;
  ranking?: number; // Alternative field name from some AI outputs
  points?: number;
  form?: string;
}

interface KeyFactsCardProps {
  keyFacts?: Record<string, KeyFact> | null;
  safeNumbers?: SafeNumbers | null;
  homeTeamName?: string;
  awayTeamName?: string;
  homeTeamLogo?: string;
  awayTeamLogo?: string;
  className?: string;
}

// Helper to safely extract rank from various field names
function extractRank(data: KeyFact | null | undefined): number | null {
  if (!data) return null;
  if (typeof data.rank === 'number') return data.rank;
  if (typeof data.ranking === 'number') return data.ranking;
  return null;
}

// Helper to safely extract points
function extractPoints(data: KeyFact | null | undefined): number | null {
  if (!data) return null;
  if (typeof data.points === 'number') return data.points;
  return null;
}

// Helper to safely extract form
function extractForm(data: KeyFact | null | undefined): string {
  if (!data) return '-';
  if (typeof data.form === 'string' && data.form.trim()) return data.form;
  return '-';
}

export function KeyFactsCard({ 
  keyFacts, 
  safeNumbers,
  homeTeamName = "Casa",
  awayTeamName = "Fora",
  homeTeamLogo,
  awayTeamLogo,
  className 
}: KeyFactsCardProps) {
  // Try to extract home data from various possible structures
  const rawHomeKeyFact = keyFacts?.home || keyFacts?.[homeTeamName];
  const rawAwayKeyFact = keyFacts?.away || keyFacts?.[awayTeamName];

  // Build normalized home data
  const homeData = {
    rank: extractRank(rawHomeKeyFact) ?? safeNumbers?.home?.rank ?? null,
    points: extractPoints(rawHomeKeyFact) ?? safeNumbers?.home?.points ?? null,
    form: extractForm(rawHomeKeyFact)
  };

  // Build normalized away data
  const awayData = {
    rank: extractRank(rawAwayKeyFact) ?? safeNumbers?.away?.rank ?? null,
    points: extractPoints(rawAwayKeyFact) ?? safeNumbers?.away?.points ?? null,
    form: extractForm(rawAwayKeyFact)
  };

  // Check if we have any meaningful data to display
  const hasHomeData = homeData.rank !== null || homeData.points !== null;
  const hasAwayData = awayData.rank !== null || awayData.points !== null;

  const { t } = useTranslation('analysis');

  if (!hasHomeData && !hasAwayData) return null;

  return (
    <SG7TacticalPanel className={className}>
      <div className="p-4">
        <h3 className="text-sm font-bold flex items-center gap-2 text-[hsl(var(--sg7-text))] mb-3">
          <TrendingUp className="h-4 w-4 text-[hsl(var(--sg7-gold))]" />
          {t('cards.keyFacts')}
        </h3>
        <div className="space-y-3">
          {hasHomeData && (
            <div className="flex items-center justify-between p-2 bg-[hsl(var(--sg7-surface-2))] rounded">
              <div className="flex items-center gap-2">
                {homeTeamLogo && <img src={homeTeamLogo} alt={homeTeamName} className="w-4 h-4 object-contain" />}
                <span className="text-sm font-medium truncate max-w-[120px] text-[hsl(var(--sg7-text))]">{homeTeamName}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                {homeData.rank !== null && (
                  <span className="text-[hsl(var(--sg7-gold))]">#{homeData.rank}</span>
                )}
                {homeData.points !== null && (
                  <span className="font-bold text-[hsl(var(--sg7-text))]">{homeData.points} pts</span>
                )}
                {homeData.form !== '-' && (
                  <span className="text-xs tracking-wider font-mono text-[hsl(var(--sg7-text-muted))]">{homeData.form}</span>
                )}
              </div>
            </div>
          )}
          {hasAwayData && (
            <div className="flex items-center justify-between p-2 bg-[hsl(var(--sg7-surface-2))] rounded">
              <div className="flex items-center gap-2">
                {awayTeamLogo && <img src={awayTeamLogo} alt={awayTeamName} className="w-4 h-4 object-contain" />}
                <span className="text-sm font-medium truncate max-w-[120px] text-[hsl(var(--sg7-text))]">{awayTeamName}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                {awayData.rank !== null && (
                  <span className="text-[hsl(var(--sg7-gold))]">#{awayData.rank}</span>
                )}
                {awayData.points !== null && (
                  <span className="font-bold text-[hsl(var(--sg7-text))]">{awayData.points} pts</span>
                )}
                {awayData.form !== '-' && (
                  <span className="text-xs tracking-wider font-mono text-[hsl(var(--sg7-text-muted))]">{awayData.form}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </SG7TacticalPanel>
  );
}
